import { EnhancedEventCard } from '@eb/eds-event-card';
import React from 'react';
import { useLazyImages } from '../../hooks';
import { trackEventClick, trackSaveEvent } from './analytics';
import {
    useAffCode,
    useSaveEvent,
    useShouldSaveFromLoggedOutRedirect,
} from './hooks';
import { EventCardProps } from './types';
import { parseFollowSignal } from './utils';

export const EventCard: React.FunctionComponent<EventCardProps> = (props) => {
    //Prop Breakdown
    const {
        eventData: {
            id: eventId,
            organizerFollowerCount,
            savedByYou,
            ...eventData
        },
        isLazyImage,
        currencyFormat,
        gaCategory,
        isAuthenticated,
        onClick,
        onSave,
        ...otherProps
    } = props;

    //Initialize document event listener for lazy loading images
    const { shouldUseLazyImages } = useLazyImages(isLazyImage);

    //Local state
    const [saved, setSaved] = React.useState(!!savedByYou);

    //Action handlers and tracking helpers
    const handleSaveEvent = useSaveEvent({
        setSaved,
        onCompleted: () => {
            onSave?.(saved, eventId);

            gaCategory &&
                isAuthenticated &&
                !onSave &&
                trackSaveEvent(gaCategory, !saved, eventId);
        },
    });

    //Currently does not display success notification
    //However save is successful
    //TODO: Ensure notification fires
    useShouldSaveFromLoggedOutRedirect(
        handleSaveEvent,
        eventId,
        isAuthenticated,
    );

    const handleOnClick = React.useCallback(() => {
        gaCategory &&
            !onClick &&
            trackEventClick(gaCategory, eventId, organizerFollowerCount);
        onClick?.(eventId);
    }, [eventId, gaCategory, onClick, organizerFollowerCount]);

    // Final data massaging
    const signalData = parseFollowSignal(organizerFollowerCount || 0);

    if (eventData.minPrice && currencyFormat) {
        eventData.minPrice.currencyFormat = currencyFormat;
    }

    eventData.url = useAffCode(eventData.url, props.affCode || '');

    return (
        <EnhancedEventCard
            //Let passed in props overwrite
            //the signal info if necessary
            {...signalData}
            {...otherProps}
            {...eventData}
            id={eventId}
            isLazyImage={shouldUseLazyImages && isLazyImage}
            isAuthenticated={isAuthenticated}
            onClick={handleOnClick}
            onSaveEvent={() =>
                handleSaveEvent({
                    eventId,
                    isAuthenticated: !!isAuthenticated,
                    savedByYou: saved,
                })
            }
            savedByYou={saved}
        />
    );
};
